export interface PMCMessage {
  type: string;
  [key: string]: any;
}

export interface PMCHandshakeMessage extends PMCMessage {
  type: 'PMC_HANDSHAKE';
  locale?: string;
  country?: string;
  currency?: string;
  cookies?: boolean | null;
  page?: string;
}

export interface PMCAddToCartMessage extends PMCMessage {
  type: 'PMC_ADD_TO_CART';
  variantId: number;
  quantity: number;
}

export interface PMCAddToCartSuccessMessage extends PMCMessage {
  type: 'PMC_ADD_TO_CART_SUCCESS';
  cartId?: string;
  cartItem?: any;
}

export interface PMCAddToCartErrorMessage extends PMCMessage {
  type: 'PMC_ADD_TO_CART_ERROR';
  error?: string;
}

export interface PMCTestModeMessage extends PMCMessage {
  type: 'PMC_TEST_MODE';
}

export type PMCMessageHandler<T extends PMCMessage = PMCMessage> = (message: T) => void;

export class ParentFrameMessagingService {
  private messageHandlers: Map<string, Set<PMCMessageHandler>> = new Map();
  private isListening = false;
  private iframeReadyRetryInterval: NodeJS.Timeout | null = null;
  private handshakeReceived = false;

  constructor() {
    // Arrow function already binds 'this'
  }

  startListening(): void {
    if (!this.isListening) {
      window.addEventListener('message', this.handleMessage);
      this.isListening = true;
    }
  }

  stopListening(): void {
    if (this.isListening) {
      window.removeEventListener('message', this.handleMessage);
      this.isListening = false;
    }
  }

  private handleMessage = (event: MessageEvent): void => {
    const message: PMCMessage = event.data;

    if (message && message.type) {
      if (message.type === 'PMC_HANDSHAKE') {
        this.handshakeReceived = true;
        this.stopIframeReadyRetry();
      }

      const handlers = this.messageHandlers.get(message.type);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(message);
          } catch (error) {
            console.error(`Error handling PMC message ${message.type}:`, error);
          }
        });
      }
    }
  };

  onMessage<T extends PMCMessage>(messageType: string, handler: PMCMessageHandler<T>): () => void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }

    const handlers = this.messageHandlers.get(messageType)!;
    handlers.add(handler as PMCMessageHandler);

    if (!this.isListening) {
      this.startListening();
    }

    return () => {
      handlers.delete(handler as PMCMessageHandler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(messageType);
      }
    };
  }

  sendToParent(message: PMCMessage): void {
    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      console.error('Failed to send message to parent frame:', error);
    }
  }

  private stopIframeReadyRetry(): void {
    if (this.iframeReadyRetryInterval) {
      clearInterval(this.iframeReadyRetryInterval);
      this.iframeReadyRetryInterval = null;
    }
  }

  sendIframeReady(): void {
    this.sendToParent({ type: 'PMC_IFRAME_READY' });
  }

  sendIframeReadyWithRetry(retryIntervalMs: number = 1500): () => void {
    this.sendIframeReady();

    if (this.handshakeReceived) {
      return () => {};
    }

    this.iframeReadyRetryInterval = setInterval(() => {
      if (this.handshakeReceived) {
        this.stopIframeReadyRetry();
        return;
      }
      this.sendIframeReady();
    }, retryIntervalMs);

    // Return cleanup function to stop retries manually
    return () => {
      this.stopIframeReadyRetry();
    };
  }

  sendActivateIframe(): void {
    this.sendToParent({ type: 'PMC_ACTIVATE_IFRAME' });
  }

  sendExpandIframe(): void {
    this.sendToParent({ type: 'PMC_EXPAND_IFRAME' });
  }

  sendCollapseIframe(): void {
    this.sendToParent({ type: 'PMC_COLLAPSE_IFRAME' });
  }

  sendRedirectToCart(): void {
    this.sendToParent({ type: 'PMC_REDIRECT_TO_CART' });
  }

  sendAddToCart(variantId: number, quantity: number = 1): void {
    this.sendToParent({
      type: 'PMC_ADD_TO_CART',
      variantId,
      quantity,
    });
  }

  onHandshake(handler: PMCMessageHandler<PMCHandshakeMessage>): () => void {
    return this.onMessage('PMC_HANDSHAKE', handler);
  }

  onTestMode(handler: PMCMessageHandler<PMCTestModeMessage>): () => void {
    return this.onMessage('PMC_TEST_MODE', handler);
  }

  onAddToCartSuccess(handler: PMCMessageHandler<PMCAddToCartSuccessMessage>): () => void {
    return this.onMessage('PMC_ADD_TO_CART_SUCCESS', handler);
  }

  onAddToCartError(handler: PMCMessageHandler<PMCAddToCartErrorMessage>): () => void {
    return this.onMessage('PMC_ADD_TO_CART_ERROR', handler);
  }

  destroy(): void {
    this.stopListening();
    this.stopIframeReadyRetry();
    this.messageHandlers.clear();
    this.handshakeReceived = false;
  }
}

// Create singleton instance
export const parentFrameMessaging = new ParentFrameMessagingService();

// Export default instance for convenience
export default parentFrameMessaging;
